import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import { useRecruitingCache } from "../../context/RecruitingCacheContext";
import { getApiEndpoint } from "../../config/config";
import "./RecruitingPage.css";
import "./RecruitingDashboard.css";
import PageContent from "../shared/PageContent/PageContent";
import CalendarScheduler from "./CalendarScheduler";
import AddLeadModal from "./AddLeadModal";
import MeetingReportModal from "./MeetingReportModal";
import DocumentStatusModal from "./DocumentStatusModal";
import SendDocsModal from "./SendDocsModal";
import MeetingDetailsModal from "./MeetingDetailsModal";
import RecruitDetailView from "./RecruitDetailView";
import LogContactModal from "./LogContactModal";
import MarkColdModal from "./MarkColdModal";
import DocumentSigningModal from "./DocumentSigningModal";
import SentDocumentsModal from "./SentDocumentsModal";
import ManageDocumentsModal from "./ManageDocumentsModal";
import AddToListModal from "./AddToListModal";
import { handleGenericError } from "../../utils/errorHandler";
// GOOGLE CALENDAR DISABLED
// import {
//   getUpcomingEvents,
//   getEventsForDateRange,
//   isSignedIn,
// } from "../../services/googleCalendar";

function RecruitingPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { getVerifiedJWT, user } = useAuth();
  const { showSuccess } = useToast();
  const { getCachedLeads, getCachedMeetings, updateMeetings, isCacheValid } =
    useRecruitingCache();
  const [activeTab, setActiveTab] = useState("Contacted");
  const [isLoadingLeads, setIsLoadingLeads] = useState(false);
  const [isLoadingMeetings, setIsLoadingMeetings] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAddLeadModalOpen, setIsAddLeadModalOpen] = useState(false);
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [isDocStatusModalOpen, setIsDocStatusModalOpen] = useState(false);
  const [isSendDocsModalOpen, setIsSendDocsModalOpen] = useState(false);
  const [isMeetingDetailsModalOpen, setIsMeetingDetailsModalOpen] =
    useState(false);
  const [isLogContactModalOpen, setIsLogContactModalOpen] = useState(false);
  const [isMarkColdModalOpen, setIsMarkColdModalOpen] = useState(false);
  const [isDocumentSigningModalOpen, setIsDocumentSigningModalOpen] =
    useState(false);
  const [isSentDocumentsModalOpen, setIsSentDocumentsModalOpen] =
    useState(false);
  const [isManageDocumentsModalOpen, setIsManageDocumentsModalOpen] =
    useState(false);
  const [isAddToListModalOpen, setIsAddToListModalOpen] = useState(false);
  const [selectedRecruit, setSelectedRecruit] = useState(null);
  const [scheduledMeetings, setScheduledMeetings] = useState({});
  const [lastMeetings, setLastMeetings] = useState({});
  const [realLeads, setRealLeads] = useState([]);
  const [viewingRecruitDetail, setViewingRecruitDetail] = useState(null);
  const [selectedRecruiters, setSelectedRecruiters] = useState([]); // Empty array = "Everyone"
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [downlineManagers, setDownlineManagers] = useState([]);

  const recruitingTabs = [
    { label: "Dashboard", path: "/recruiting" },
    { label: "Pipeline", path: "/recruiting/pipeline" },
    { label: "Lists", path: "/recruiting/list" },
    { label: "Documents", path: "/recruiting/documents" },
  ];

  // Ref for RecruitDetailView to call refreshHistory
  const recruitDetailRef = useRef(null);

  // Ref to store the auto-refresh interval so we can pause it during critical operations
  const refreshIntervalRef = useRef(null);

  //  Handle URL parameters for opening SendDocsModal with pre-selected template
  const [preSelectedTemplateId, setPreSelectedTemplateId] = useState(null);
  const hasHandledUrlParams = useRef(false);

  // PERFORMANCE: Memoize filtered leads to prevent re-filtering on every render
  const filteredLeads = useMemo(
    () =>
      selectedRecruiters.length > 0
        ? realLeads.filter(lead =>
            selectedRecruiters.includes(lead.recruiter_name)
          )
        : realLeads,
    [realLeads, selectedRecruiters]
  );

  // PERFORMANCE: Memoize recruitsByStage to prevent re-filtering on every render
  const recruitsByStage = useMemo(
    () => ({
      Contacted: filteredLeads.filter(lead => lead.status === "Lead"),
      "1st Meeting Occurred": filteredLeads.filter(
        lead => lead.status === "Initial Meeting"
      ),
      "2nd Meeting Occurred": filteredLeads.filter(
        lead => lead.status === "Second Meeting"
      ),
      "Additional Meetings Occurred": filteredLeads.filter(
        lead => lead.status === "Additional Meetings"
      ),
      "Offers Sent": filteredLeads.filter(
        lead => lead.status === "Offers Sent"
      ),
      Signed: filteredLeads.filter(lead => lead.status === "Signed"),
    }),
    [filteredLeads]
  );

  // PERFORMANCE: Memoize recruitment stages to prevent recalculation
  const recruitmentStages = useMemo(
    () => [
      {
        name: "Contacted",
        count: recruitsByStage["Contacted"]?.length || 0,
        icon: "trending_up",
      },
      {
        name: "1st Meeting Occurred",
        count: recruitsByStage["1st Meeting Occurred"]?.length || 0,
        icon: "event",
      },
      {
        name: "2nd Meeting Occurred",
        count: recruitsByStage["2nd Meeting Occurred"]?.length || 0,
        icon: "event_repeat",
      },
      {
        name: "Additional Meetings Occurred",
        count: recruitsByStage["Additional Meetings Occurred"]?.length || 0,
        icon: "groups",
      },
      {
        name: "Offers Sent",
        count: recruitsByStage["Offers Sent"]?.length || 0,
        icon: "description",
      },
      {
        name: "Signed",
        count: recruitsByStage["Signed"]?.length || 0,
        icon: "check_circle",
      },
    ],
    [recruitsByStage]
  );

  // PERFORMANCE: Memoize active recruits to prevent recalculation
  const activeRecruits = useMemo(
    () => recruitsByStage[activeTab] || [],
    [recruitsByStage, activeTab]
  );

  // Fetch recruiting leads from API
  const fetchLeads = async (isBackgroundRefresh = false) => {
    try {
      const jwt = await getVerifiedJWT();
      if (!jwt) return null;

      // Check if we're filtering by list
      const listId = searchParams.get("listId");
      const endpoint = listId
        ? `/v1/recruiting/lists/${listId}/leads`
        : "/v1/recruiting/leads";

      const response = await fetch(getApiEndpoint(endpoint), {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
      });

      if (response.ok) {
        const result = await response.json();
        const freshLeads = result.data || [];

        setRealLeads(freshLeads);
        return freshLeads; // Return the fresh leads array
      }
    } catch (error) {}
    return null;
  };

  // Get list info from URL params
  const listId = searchParams.get("listId");
  const listName = searchParams.get("listName");
  const isFilteredByList = !!listId;

  // Handler to clear list filter
  const handleClearListFilter = () => {
    navigate("/recruiting/pipeline");
  };

  // Helper function to check if manager has accepted invitation
  // const hasManagerAccepted = attendees => {
  //   if (!attendees || attendees.length === 0) {
  //     return true; // No attendees = no pending status
  //   }

  //   // Check if any attendee is still pending or has declined
  //   return !attendees.some(
  //     att =>
  //       att.responseStatus === "needsAction" ||
  //       att.responseStatus === "declined"
  //   );
  // };

  // Fetch scheduled meetings from local database - OPTIMIZED VERSION
  const fetchScheduledMeetings = async (leadsData = null) => {
    try {
      setIsLoadingMeetings(true);
      const jwt = await getVerifiedJWT();
      if (!jwt) {
        setIsLoadingMeetings(false);
        return;
      }

      // Use provided leads data or fall back to current state
      const currentLeads = leadsData || realLeads;

      // Create a Map for O(1) lookups instead of O(n) array.find
      const leadsMap = new Map(currentLeads.map(lead => [lead.id, lead]));

      // Fetch from local database
      const response = await fetch(
        getApiEndpoint("/v1/recruiting/upcoming-meetings"),
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
        }
      );

      if (!response.ok) {
        setIsLoadingMeetings(false);
        return;
      }

      const result = await response.json();
      const localMeetings = result.data || [];

      // Process meetings efficiently
      const now = new Date();
      const nextMeetings = {};
      const pastMeetings = {};

      // Group meetings by lead_id
      const meetingsByLead = {};
      localMeetings.forEach(meeting => {
        const recruitId = meeting.lead_id;

        // Quick validation: lead must exist
        if (!leadsMap.has(recruitId)) return;

        if (!meetingsByLead[recruitId]) {
          meetingsByLead[recruitId] = [];
        }
        meetingsByLead[recruitId].push({
          time: new Date(meeting.time),
          duration: 30,
          eventId: null,
          attendees: [],
          managerAccepted: true,
          source: "local",
        });
      });

      // Process each lead's meetings
      Object.entries(meetingsByLead).forEach(([recruitId, meetingsList]) => {
        const recruitIdNum = parseInt(recruitId);
        const recruit = leadsMap.get(recruitIdNum);

        // Find next upcoming meeting (earliest future)
        let nextMeeting = null;
        let nextTime = null;

        for (const meeting of meetingsList) {
          if (meeting.time >= now) {
            if (!nextTime || meeting.time < nextTime) {
              nextTime = meeting.time;
              nextMeeting = meeting;
            }
          }
        }

        if (nextMeeting) {
          nextMeetings[recruitId] = nextMeeting;
        }

        // Find most recent past unreported meeting
        let lastMeeting = null;
        let lastTime = null;
        const recruitLastMeetingDate = recruit?.last_meeting_date
          ? new Date(recruit.last_meeting_date)
          : null;

        for (const meeting of meetingsList) {
          if (meeting.time < now) {
            // Check if meeting has been reported
            const hasBeenReported =
              recruitLastMeetingDate && recruitLastMeetingDate >= meeting.time;

            if (!hasBeenReported) {
              if (!lastTime || meeting.time > lastTime) {
                lastTime = meeting.time;
                lastMeeting = meeting;
              }
            }
          }
        }

        if (lastMeeting) {
          pastMeetings[recruitId] = lastMeeting;
        }
      });

      setScheduledMeetings(nextMeetings);
      setLastMeetings(pastMeetings);

      // Update cache with meeting data
      updateMeetings({
        scheduled: nextMeetings,
        last: pastMeetings,
      });
      setIsLoadingMeetings(false);
    } catch (error) {
      // Fail silently
      setIsLoadingMeetings(false);
    }
  };

  useEffect(() => {
    // INSTANT LOAD: Check for cached data first
    const cachedLeads = getCachedLeads();
    const cachedMeetingsData = getCachedMeetings();
    const preloadedLeads = location.state?.preloadedLeads;
    const listId = searchParams.get("listId");

    // Priority 1: Preloaded data (from navigation)
    if (preloadedLeads && preloadedLeads.length > 0) {
      const filtered = listId
        ? preloadedLeads.filter(lead =>
            lead.lists?.some(list => list.id === parseInt(listId))
          )
        : preloadedLeads;
      setRealLeads(filtered);

      // Load cached meetings if available
      if (cachedMeetingsData) {
        setScheduledMeetings(cachedMeetingsData.scheduled || {});
        setLastMeetings(cachedMeetingsData.last || {});
      }
    }
    // Priority 2: Cached data (instant from localStorage)
    else if (cachedLeads && isCacheValid()) {
      const filtered = listId
        ? cachedLeads.filter(lead =>
            lead.lists?.some(list => list.id === parseInt(listId))
          )
        : cachedLeads;
      setRealLeads(filtered);

      // Load cached meetings if available
      if (cachedMeetingsData) {
        setScheduledMeetings(cachedMeetingsData.scheduled || {});
        setLastMeetings(cachedMeetingsData.last || {});
      }
    }

    // Fetch fresh data in background (parallel, non-blocking)
    // const fetchFreshData = async () => {
    //   setIsLoadingLeads(true);

    //   try {
    //     const jwt = await getVerifiedJWT();
    //     if (!jwt) return;

    //     const endpoint = listId
    //       ? `/recruiting/lists/${listId}/leads`
    //       : "/recruiting/leads";

    //     // Fetch leads
    //     const leadsPromise = fetch(getApiEndpoint(endpoint), {
    //       method: "GET",
    //       headers: {
    //         "Content-Type": "application/json",
    //         Authorization: `Bearer ${jwt}`,
    //       },
    //     });

    //     // Wait for leads to complete
    //     const leadsResponse = await leadsPromise;

    //     if (leadsResponse.ok) {
    //       const result = await leadsResponse.json();
    //       const freshLeads = result.data || [];

    //       const finalLeads = listId
    //         ? freshLeads.filter(lead =>
    //             lead.lists?.some(list => list.id === parseInt(listId))
    //           )
    //         : freshLeads;

    //       setRealLeads(finalLeads);
    //       updateLeads(freshLeads); // Update cache
    //     }
    //   } catch (error) {
    //   } finally {
    //     setIsLoadingLeads(false);
    //   }
    // };

    // OPTIMIZED: Use the new combined pipeline-data endpoint for single API call
    const loadInitialData = async () => {
      try {
        const jwt = await getVerifiedJWT();
        if (!jwt) return;

        const listId = searchParams.get("listId");

        // Use the optimized combined endpoint for initial load
        const response = await fetch(
          getApiEndpoint("/v1/recruiting/pipeline-data"),
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${jwt}`,
            },
          }
        );

        if (response.ok) {
          const result = await response.json();
          const { leads: freshLeads, meetings: freshMeetings } = result.data;

          // Filter by list if needed
          const filteredLeads = listId
            ? freshLeads.filter(lead =>
                lead.lists?.some(list => list.id === parseInt(listId))
              )
            : freshLeads;

          setRealLeads(filteredLeads);

          // Process meetings data efficiently
          const now = new Date();
          const nextMeetings = {};
          const pastMeetings = {};

          // Create a Map for O(1) lookups
          const leadsMap = new Map(filteredLeads.map(lead => [lead.id, lead]));

          // Group meetings by lead_id
          const meetingsByLead = {};
          freshMeetings.forEach(meeting => {
            const recruitId = meeting.lead_id;
            if (!leadsMap.has(recruitId)) return;

            if (!meetingsByLead[recruitId]) {
              meetingsByLead[recruitId] = [];
            }
            meetingsByLead[recruitId].push({
              time: new Date(meeting.time),
              duration: 30,
              eventId: null,
              attendees: [],
              managerAccepted: true,
              source: meeting.source,
            });
          });

          // Process each lead's meetings
          Object.entries(meetingsByLead).forEach(
            ([recruitId, meetingsList]) => {
              const recruitIdNum = parseInt(recruitId);
              const recruit = leadsMap.get(recruitIdNum);

              // Find next upcoming meeting
              let nextMeeting = null;
              let nextTime = null;
              for (const meeting of meetingsList) {
                if (meeting.time >= now) {
                  if (!nextTime || meeting.time < nextTime) {
                    nextTime = meeting.time;
                    nextMeeting = meeting;
                  }
                }
              }
              if (nextMeeting) {
                nextMeetings[recruitId] = nextMeeting;
              }

              // Find most recent past unreported meeting
              let lastMeeting = null;
              let lastTime = null;
              const recruitLastMeetingDate = recruit?.last_meeting_date
                ? new Date(recruit.last_meeting_date)
                : null;

              for (const meeting of meetingsList) {
                if (meeting.time < now) {
                  const hasBeenReported =
                    recruitLastMeetingDate &&
                    recruitLastMeetingDate >= meeting.time;

                  if (!hasBeenReported) {
                    if (!lastTime || meeting.time > lastTime) {
                      lastTime = meeting.time;
                      lastMeeting = meeting;
                    }
                  }
                }
              }
              if (lastMeeting) {
                pastMeetings[recruitId] = lastMeeting;
              }
            }
          );

          setScheduledMeetings(nextMeetings);
          setLastMeetings(pastMeetings);
          setIsLoadingMeetings(false);
        }
      } catch (error) {
        // Error loading pipeline data - fail silently
      }
    };
    loadInitialData();

    // Auto-refresh disabled: data is loaded on mount and after user actions
    // only, so the table no longer reloads periodically on its own.

    // Handle browser back button
    const handlePopState = event => {
      if (viewingRecruitDetail) {
        setViewingRecruitDetail(null);
      }
    };

    window.addEventListener("popstate", handlePopState);

    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
      window.removeEventListener("popstate", handlePopState);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewingRecruitDetail, searchParams]);

  useEffect(() => {
    const openSendDocs = searchParams.get("openSendDocs");
    const templateId = searchParams.get("templateId");

    if (openSendDocs === "true" && templateId && !hasHandledUrlParams.current) {
      hasHandledUrlParams.current = true;
      // Store the template ID and open the SendDocsModal immediately
      setPreSelectedTemplateId(templateId);
      setIsSendDocsModalOpen(true);

      // Clear the URL parameters after a delay to allow modal to fully open
      setTimeout(() => {
        const newUrl = "/recruiting/pipeline";
        window.history.replaceState({}, "", newUrl);
      }, 500);
    }
  }, [searchParams]);

  // Reset the URL params handler when modal closes
  useEffect(() => {
    if (!isSendDocsModalOpen) {
      hasHandledUrlParams.current = false;
    }
  }, [isSendDocsModalOpen]);

  // Extract unique recruiters from leads data for filter dropdown
  useEffect(() => {
    if (realLeads.length > 0 && user) {
      const uniqueNames = Array.from(
        new Set(realLeads.map(lead => lead.recruiter_name))
      ).filter(Boolean); // Remove any null/undefined names

      // Use firstName and lastName (not first_name, last_name)
      const currentUserName = `${user.firstName} ${user.lastName}`.trim();

      // Separate current user from others (case-insensitive matching)
      const currentUserEntry = uniqueNames.find(
        name => name.toLowerCase().trim() === currentUserName.toLowerCase()
      );
      const otherManagers = uniqueNames
        .filter(
          name => name.toLowerCase().trim() !== currentUserName.toLowerCase()
        )
        .sort((a, b) => a.localeCompare(b)); // Alphabetically sort others

      // Build final list: current user first, then others - DON'T sort after this!
      const finalList = [];

      // Add current user first with explicit sortIndex
      if (currentUserEntry) {
        finalList.push({
          name: currentUserEntry,
          rep_id: 0,
          sortIndex: 0,
          isCurrentUser: true,
        });
      }

      // Add others with sequential sortIndex
      otherManagers.forEach((name, idx) => {
        finalList.push({
          name: name,
          rep_id: idx + 1,
          sortIndex: idx + 1,
          isCurrentUser: false,
        });
      });

      setDownlineManagers(finalList);
    } else {
      setDownlineManagers([]);
    }
  }, [realLeads, user]);

  // Handler to open meeting scheduler modal
  const handleScheduleMeeting = (recruit, shouldAdvanceAfter = false) => {
    setSelectedRecruit(recruit);
    // Store whether to advance stage after scheduling
    setSelectedRecruit(prev => ({
      ...recruit,
      _shouldAdvanceAfter: shouldAdvanceAfter,
    }));
    setIsModalOpen(true);
  };

  // Get existing event ID for a recruit if they have a scheduled meeting
  const getExistingEventId = recruitId => {
    return scheduledMeetings[recruitId]?.eventId || null;
  };

  // Handler to close modal and save meeting info
  const handleCloseModal = async meetingInfo => {
    const shouldAdvance = selectedRecruit?._shouldAdvanceAfter;
    setIsModalOpen(false);

    // If a meeting was scheduled, refresh the meetings from Google Calendar
    if (meetingInfo?.scheduled && meetingInfo?.recruitId) {
      // OPTIMISTIC UPDATE: Update only the meeting data immediately
      setScheduledMeetings(prev => ({
        ...prev,
        [meetingInfo.recruitId]: {
          time: meetingInfo.meetingTime,
          duration: meetingInfo.duration,
          eventId: meetingInfo.eventId,
          managerAccepted: true,
        },
      }));

      const recruit = realLeads.find(r => r.id === meetingInfo.recruitId);

      // NOTE: Schedule-meeting API is already called in CalendarScheduler.js
      // No need to call it again here - it would create a duplicate event

      // If scheduling from "Schedule Follow-up" with meeting occurred checked
      if (shouldAdvance && recruit) {
        await handleAdvanceStage(recruit);
      } else {
        // Don't auto-advance - user must report meeting first to move stages
        showSuccess("Meeting scheduled successfully");
      }

      // PERFORMANCE FIX: Don't refresh ALL leads - just update the meeting data
      // The optimistic update above is sufficient for immediate UI feedback
      // Background refresh only for cache sync (no UI update needed)
      setTimeout(async () => {
        try {
          // Only update the cache in background, don't trigger state updates
          const jwt = await getVerifiedJWT();
          if (!jwt) return;

          const response = await fetch(
            getApiEndpoint("/v1/recruiting/upcoming-meetings"),
            {
              method: "GET",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${jwt}`,
              },
            }
          );

          if (response.ok) {
            const result = await response.json();
            const freshMeetings = result.data || [];

            // Process and update cache silently
            const now = new Date();
            const nextMeetings = {};
            const pastMeetings = {};

            freshMeetings.forEach(meeting => {
              const recruitId = meeting.lead_id;
              const meetingTime = new Date(meeting.time);

              if (meetingTime >= now) {
                if (
                  !nextMeetings[recruitId] ||
                  meetingTime < nextMeetings[recruitId].time
                ) {
                  nextMeetings[recruitId] = {
                    time: meetingTime,
                    duration: 30,
                    eventId: null,
                    attendees: [],
                    managerAccepted: true,
                    source: meeting.source,
                  };
                }
              }
            });

            // Update cache only (no state update = no re-render)
            updateMeetings({
              scheduled: nextMeetings,
              last: pastMeetings,
            });
          }
        } catch (error) {
          // Fail silently - optimistic update is already showing
        }
      }, 1500);

      // Refresh history in detail view if viewing that recruit
      if (
        viewingRecruitDetail &&
        viewingRecruitDetail.id === meetingInfo.recruitId
      ) {
        recruitDetailRef.current?.refreshHistory();
      }
    }

    setSelectedRecruit(null);
  };

  // Update lead status via API
  const updateLeadStatus = async (leadId, newStatus) => {
    try {
      const jwt = await getVerifiedJWT();
      if (!jwt) return false;

      const response = await fetch(
        getApiEndpoint(`/v2/recruiting/leads/${leadId}/status`),
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
          body: JSON.stringify({ status: newStatus }),
        }
      );

      if (response.ok) {
        return true;
      }
      return false;
    } catch (error) {
      return false;
    }
  };

  // Handler to open report modal
  const handleReportMeeting = recruit => {
    setSelectedRecruit(recruit);
    setIsReportModalOpen(true);
  };

  // Determine next stage when advancing after successful meeting
  const getNextStage = currentStatus => {
    const stageProgression = {
      Lead: "Initial Meeting", // First meeting - moves from Contacted to 1st Meeting Occurred
      "Initial Meeting": "Second Meeting",
      "Second Meeting": "Additional Meetings",
      "Additional Meetings": "Additional Meetings", // Stay in this stage
    };
    return stageProgression[currentStatus] || currentStatus;
  };

  // Handler to advance recruit to next stage
  const handleAdvanceStage = async recruit => {
    if (!recruit) return false;

    const nextStatus = getNextStage(recruit.status);
    const previousStatus = recruit.status;

    // If already at final meeting stage, don't advance
    if (nextStatus === recruit.status) {
      return true;
    }

    // Determine which tab name to switch to
    const tabMap = {
      "Initial Meeting": "1st Meeting Occurred", // First meeting completed
      "Second Meeting": "2nd Meeting Occurred",
      "Additional Meetings": "Additional Meetings Occurred",
    };

    const targetTab = tabMap[nextStatus];

    // Optimistic update - update UI immediately
    setRealLeads(prev =>
      prev.map(lead =>
        lead.id === recruit.id ? { ...lead, status: nextStatus } : lead
      )
    );
    if (targetTab) {
      setActiveTab(targetTab);
      showSuccess(`Lead moved to ${targetTab} stage`);
    }

    // Update server in background
    const success = await updateLeadStatus(recruit.id, nextStatus);
    if (!success) {
      // Revert on failure
      setRealLeads(prev =>
        prev.map(lead =>
          lead.id === recruit.id ? { ...lead, status: previousStatus } : lead
        )
      );
      const previousTab = tabMap[previousStatus] || "Contacted";
      setActiveTab(previousTab);
      handleGenericError("Failed to update status", "Failed to update status");
      return false;
    }

    return true;
  };

  // Handler when document is sent successfully
  const handleDocumentSent = async recruit => {
    if (!recruit) return;

    // Optimistic update - update UI immediately
    setRealLeads(prev =>
      prev.map(lead =>
        lead.id === recruit.id ? { ...lead, status: "Offers Sent" } : lead
      )
    );
    setActiveTab("Offers Sent");
    showSuccess(`${recruit.name} moved to Offers Sent stage`);

    // Refresh leads data to get the updated status from the server
    // The backend already updates the status when sendDocument is called
    try {
      await fetchLeads();

      // Refresh history in detail view if viewing that recruit
      if (viewingRecruitDetail && viewingRecruitDetail.id === recruit.id) {
        recruitDetailRef.current?.refreshHistory();
      }
    } catch (error) {
      // If refresh fails, the optimistic update will remain
      // No need to show error since the document was sent successfully
    }
  };

  // Handler to close report modal
  const handleCloseReportModal = async (reportSaved, reportData) => {
    setIsReportModalOpen(false);
    if (reportSaved && reportData && selectedRecruit) {
      // Optimistic update - immediately update the recruit's last_meeting_date
      const updatedRecruitId = selectedRecruit.id;
      const reportedMeetingTime = new Date(reportData.meeting_date);

      setRealLeads(prev =>
        prev.map(lead =>
          lead.id === updatedRecruitId
            ? { ...lead, last_meeting_date: reportedMeetingTime }
            : lead
        )
      );

      // Remove from lastMeetings immediately so Report button disappears
      setLastMeetings(prev => {
        const updated = { ...prev };
        delete updated[updatedRecruitId];
        return updated;
      });

      // Refresh history in detail view if viewing that recruit
      if (
        viewingRecruitDetail &&
        selectedRecruit &&
        viewingRecruitDetail.id === selectedRecruit.id
      ) {
        recruitDetailRef.current?.refreshHistory();
      }

      // Background refresh - don't wait, don't block UI
      setTimeout(async () => {
        const freshLeads = await fetchLeads();
        if (freshLeads) {
          await fetchScheduledMeetings(freshLeads);
        }
      }, 500); // Wait longer to ensure DB has processed
    }
    setSelectedRecruit(null);
  };

  // Handler for when a new lead is added
  const handleLeadAdded = async newLead => {
    setIsLoadingLeads(true);
    try {
      // Refresh the leads list first
      await fetchLeads();
      // Then refresh meetings with updated leads
      await fetchScheduledMeetings(realLeads);
    } finally {
      setIsLoadingLeads(false);
    }
  };

  // Handler to view meeting details
  const handleViewMeetingDetails = recruit => {
    setSelectedRecruit(recruit);
    setIsMeetingDetailsModalOpen(true);
  };

  // Handler to mark recruit as cold
  const handleMarkCold = async (recruit, e) => {
    if (e) e.stopPropagation();

    if (!recruit) return;

    // Open the confirmation modal instead of using window.confirm
    setSelectedRecruit(recruit);
    setIsMarkColdModalOpen(true);
  };

  // Handler for when the modal confirms marking recruit as cold
  const handleConfirmMarkCold = async () => {
    setIsLoadingLeads(true);
    try {
      showSuccess(`${selectedRecruit.name} has been moved to your cold list`);

      await fetchLeads(); // Refresh the leads list
      await fetchScheduledMeetings(realLeads); // Refresh meetings after leads update
    } catch (error) {
      handleGenericError(error, "Failed to mark as cold");
    } finally {
      setIsLoadingLeads(false);
      setSelectedRecruit(null);
    }
  };

  // Handler when documents are signed successfully
  const handleDocumentsSigned = async recruit => {
    if (!recruit) return;

    const previousStatus = recruit.status;

    // Optimistic update - update UI immediately
    setRealLeads(prev =>
      prev.map(lead =>
        lead.id === recruit.id ? { ...lead, status: "Signed" } : lead
      )
    );
    setActiveTab("Signed");
    showSuccess(`${recruit.name} has been marked as signed!`);

    // Update server in background - this was already handled in the modal
    // The modal makes the API call, so we don't need to do it again here

    // Refresh leads to ensure consistency
    try {
      await fetchLeads();
    } catch (error) {
      // If refresh fails, revert the optimistic update
      setRealLeads(prev =>
        prev.map(lead =>
          lead.id === recruit.id ? { ...lead, status: previousStatus } : lead
        )
      );
      handleGenericError(error, "Failed to refresh lead status");
    }
  };

  // Handler for document actions from ManageDocumentsModal
  const handleDocumentAction = async (action, recruit) => {
    try {
      switch (action) {
        case "sendESignature":
          // Open the existing SendDocsModal for eSignature
          setIsManageDocumentsModalOpen(false);
          // CRITICAL FIX: Must set selectedRecruit BEFORE opening modal
          setSelectedRecruit(recruit);
          setIsSendDocsModalOpen(true);
          break;

        case "markDocsSent":
          // Update lead status to "Offers Sent"
          const success = await updateLeadStatus(recruit.id, "Offers Sent");
          if (success) {
            showSuccess(`${recruit.name} moved to Offers Sent`);
            setRealLeads(prev =>
              prev.map(lead =>
                lead.id === recruit.id
                  ? { ...lead, status: "Offers Sent" }
                  : lead
              )
            );
            setActiveTab("Offers Sent");
          } else {
            handleGenericError(
              "Failed to update status",
              "Failed to update status"
            );
          }
          break;

        case "markSigned":
          // Update lead status to "Signed"
          const signedSuccess = await updateLeadStatus(recruit.id, "Signed");
          if (signedSuccess) {
            showSuccess(`${recruit.name} marked as signed!`);
            setRealLeads(prev =>
              prev.map(lead =>
                lead.id === recruit.id ? { ...lead, status: "Signed" } : lead
              )
            );
            setActiveTab("Signed");
          } else {
            handleGenericError(
              "Failed to update status",
              "Failed to update status"
            );
          }
          break;

        default:
          break;
      }
    } catch (error) {
      handleGenericError(error, "An error occurred");
    }
  };

  // If viewing a recruit detail, show the detail view
  if (viewingRecruitDetail) {
    return (
      <PageContent title="Recruiting">
        <RecruitDetailView
          ref={recruitDetailRef}
          recruit={viewingRecruitDetail}
          onBack={() => setViewingRecruitDetail(null)}
          onSendOffer={() => {
            setSelectedRecruit(viewingRecruitDetail);
            setIsSendDocsModalOpen(true);
          }}
          scheduledMeeting={scheduledMeetings[viewingRecruitDetail.id]}
        />
      </PageContent>
    );
  }

  return (
    <PageContent
      customHeader={
        <div className="recruiting-page-custom-header">
          <div className="recruiting-page-header-left">
            <h1 className="recruiting-page-main-title">Recruiting</h1>
            <div className="recruiting-page-nav-tabs">
              {recruitingTabs.map(tab => (
                <button
                  key={tab.path}
                  className={`recruiting-page-nav-tab ${
                    location.pathname === tab.path ? "active" : ""
                  }`}
                  onClick={() => navigate(tab.path)}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          <button
            className="btn btn-lg btn-primary"
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.625rem",
              padding: "0.75rem 1.5rem",
              fontSize: "1rem",
              fontWeight: "600",
            }}
            onClick={() => setIsAddLeadModalOpen(true)}
          >
            <span className="material-icons" style={{ fontSize: "1.5rem" }}>
              add
            </span>
            <span>Add Lead</span>
          </button>
        </div>
      }
    >
      {/* Recruiting Section */}
      <div className="recruiting-pipeline-section">
        {/* List Filter Indicator */}
        {isFilteredByList && (
          <div
            style={{
              backgroundColor: "#eff6ff",
              border: "1px solid #3b82f6",
              borderRadius: "8px",
              padding: "12px 16px",
              marginBottom: "16px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <span
                className="material-icons"
                style={{ color: "#3b82f6", fontSize: "20px" }}
              >
                folder
              </span>
              <span style={{ fontWeight: "600", color: "#1e40af" }}>
                Viewing list: {listName}
              </span>
              <span style={{ color: "#6b7280", fontSize: "14px" }}>
                ({realLeads.length} reps)
              </span>
            </div>
            <button
              onClick={handleClearListFilter}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                padding: "6px 12px",
                backgroundColor: "white",
                border: "1px solid #d1d5db",
                borderRadius: "6px",
                cursor: "pointer",
                fontSize: "14px",
                fontWeight: "500",
                color: "#374151",
                transition: "all 0.2s",
              }}
              onMouseEnter={e => {
                e.currentTarget.style.backgroundColor = "#f3f4f6";
                e.currentTarget.style.borderColor = "#9ca3af";
              }}
              onMouseLeave={e => {
                e.currentTarget.style.backgroundColor = "white";
                e.currentTarget.style.borderColor = "#d1d5db";
              }}
            >
              <span className="material-icons" style={{ fontSize: "18px" }}>
                close
              </span>
              <span>View Pipeline</span>
            </button>
          </div>
        )}

        {/* Stat Cards with Action Buttons */}
        <div className="recruiting-header-row">
          <div className="recruiting-stats-cards">
            {recruitmentStages.map(stage => (
              <div
                key={stage.name}
                className={`recruiting-stat-card ${activeTab === stage.name ? "active" : ""}`}
                onClick={() => setActiveTab(stage.name)}
              >
                <div className="recruiting-stat-icon">
                  <span className="material-icons">{stage.icon}</span>
                </div>
                <div className="recruiting-stat-content">
                  <div className="recruiting-stat-label">{stage.name}</div>
                  <div className="recruiting-stat-value">{stage.count}</div>
                </div>
              </div>
            ))}
          </div>
          <div className="recruiting-action-buttons-wrapper">
            <div style={{ position: "relative" }}>
              <button
                className="btn btn-md btn-outline-primary"
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                }}
                onClick={() => setShowFilterDropdown(!showFilterDropdown)}
              >
                <span className="material-icons" style={{ fontSize: "1.2rem" }}>
                  filter_list
                </span>
                <span>
                  {selectedRecruiters.length === 0
                    ? "Everyone"
                    : selectedRecruiters.length === 1
                      ? selectedRecruiters[0]
                      : `${selectedRecruiters.length} Managers`}
                </span>
                <span className="material-icons" style={{ fontSize: "1.2rem" }}>
                  {showFilterDropdown ? "expand_less" : "expand_more"}
                </span>
              </button>
              {showFilterDropdown && (
                <div
                  style={{
                    position: "absolute",
                    top: "calc(100% + 0.5rem)",
                    right: 0,
                    backgroundColor: "white",
                    border: "1px solid #e5e7eb",
                    borderRadius: "8px",
                    boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                    minWidth: "250px",
                    maxHeight: "400px",
                    overflowY: "auto",
                    zIndex: 1000,
                  }}
                >
                  <div
                    style={{
                      padding: "0.75rem 1rem",
                      cursor: "pointer",
                      borderBottom: "1px solid #e5e7eb",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      fontWeight:
                        selectedRecruiters.length === 0 ? "600" : "400",
                      backgroundColor:
                        selectedRecruiters.length === 0 ? "#f3f4f6" : "white",
                    }}
                    onClick={() => {
                      setSelectedRecruiters([]);
                    }}
                    onMouseEnter={e => {
                      if (selectedRecruiters.length > 0) {
                        e.currentTarget.style.backgroundColor = "#f9fafb";
                      }
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.backgroundColor =
                        selectedRecruiters.length === 0 ? "#f3f4f6" : "white";
                    }}
                  >
                    <span>Everyone</span>
                    <span style={{ color: "#6b7280", fontSize: "0.875rem" }}>
                      {realLeads.length}
                    </span>
                  </div>
                  {downlineManagers.map(manager => {
                    const leadCount = realLeads.filter(
                      lead => lead.recruiter_name === manager.name
                    ).length;
                    const isSelected = selectedRecruiters.includes(
                      manager.name
                    );
                    return (
                      <div
                        key={manager.rep_id}
                        style={{
                          padding: "0.75rem 1rem",
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          gap: "0.75rem",
                          backgroundColor: isSelected ? "#f3f4f6" : "white",
                        }}
                        onClick={() => {
                          setSelectedRecruiters(prev =>
                            prev.includes(manager.name)
                              ? prev.filter(name => name !== manager.name)
                              : [...prev, manager.name]
                          );
                        }}
                        onMouseEnter={e => {
                          if (!isSelected) {
                            e.currentTarget.style.backgroundColor = "#f9fafb";
                          }
                        }}
                        onMouseLeave={e => {
                          e.currentTarget.style.backgroundColor = isSelected
                            ? "#f3f4f6"
                            : "white";
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => {}}
                          style={{ cursor: "pointer" }}
                        />
                        <span style={{ flex: 1 }}>
                          {manager.name}
                          {manager.isCurrentUser && (
                            <span
                              style={{
                                color: "#059669",
                                fontSize: "0.75rem",
                                marginLeft: "0.5rem",
                              }}
                            >
                              (You)
                            </span>
                          )}
                        </span>
                        <span
                          style={{ color: "#6b7280", fontSize: "0.875rem" }}
                        >
                          {leadCount}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            {/* <button
              className="btn btn-md btn-outline-primary"
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
              }}
            >
              <span className="material-icons" style={{ fontSize: "1.2rem" }}>
                sort
              </span>
              <span>Sort By</span>
            </button> */}
          </div>
        </div>

        {/* Recruits Table */}
        <div
          className="recruits-table-container"
          style={{ position: "relative" }}
        >
          {isLoadingLeads && realLeads.length === 0 ? (
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <div className="loading-text">Loading leads...</div>
            </div>
          ) : (
            <>
              <table className="recruits-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Recruiter</th>
                    <th>Next Meeting</th>
                    <th>Last Contact</th>
                    <th>Last Meeting</th>
                    <th style={{ width: "60px", textAlign: "center" }}>Deal</th>
                    <th style={{ width: "60px", textAlign: "center" }}>Docs</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {activeRecruits.map(recruit => {
                    const meeting = scheduledMeetings[recruit.id];
                    const lastMeeting = lastMeetings[recruit.id];
                    const recruitDeals = Array.isArray(recruit.deals)
                      ? recruit.deals
                      : [];
                    const hasDeal = recruitDeals.length > 0;
                    const hasDocs = recruitDeals.some(d => d.document != null);
                    return (
                      <tr
                        key={recruit.id}
                        onClick={() => {
                          setViewingRecruitDetail(recruit);
                          // Update browser history to enable back button
                          window.history.pushState(
                            { page: "recruit-detail", recruitId: recruit.id },
                            "",
                            window.location.pathname
                          );
                        }}
                        style={{ cursor: "pointer" }}
                      >
                        <td>{recruit.name}</td>
                        <td>{recruit.recruiter_name || "N/A"}</td>
                        <td
                          className="clickable-cell"
                          onClick={e => {
                            e.stopPropagation();
                            if (!isLoadingMeetings) {
                              if (
                                meeting &&
                                new Date(meeting.time) >= new Date()
                              ) {
                                handleViewMeetingDetails(recruit);
                              } else if (
                                meeting &&
                                new Date(meeting.time) < new Date()
                              ) {
                                // Past meeting - do nothing or handle differently
                                handleViewMeetingDetails(recruit);
                              } else {
                                handleScheduleMeeting(recruit);
                              }
                            }
                          }}
                        >
                          {isLoadingMeetings ? (
                            <span
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "6px",
                                color: "#6b7280",
                              }}
                            >
                              <div
                                className="loading-spinner"
                                style={{
                                  width: "16px",
                                  height: "16px",
                                  border: "2px solid #e5e7eb",
                                  borderTopColor: "#3b82f6",
                                }}
                              ></div>
                              <span style={{ fontSize: "14px" }}>
                                Loading...
                              </span>
                            </span>
                          ) : meeting &&
                            new Date(meeting.time) >= new Date() ? (
                            <span
                              className="meeting-scheduled"
                              style={{
                                color: "#059669",
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "6px",
                              }}
                            >
                              <span>
                                {new Date(meeting.time).toLocaleDateString(
                                  "en-US",
                                  {
                                    month: "short",
                                    day: "numeric",
                                    year: "numeric",
                                  }
                                )}{" "}
                                {new Date(meeting.time).toLocaleTimeString(
                                  "en-US",
                                  {
                                    hour: "numeric",
                                    minute: "2-digit",
                                    hour12: true,
                                  }
                                )}
                              </span>
                              {!meeting.managerAccepted && (
                                <span
                                  className="pending-indicator"
                                  title="Manager hasn't confirmed yet"
                                  style={{
                                    color: "#ef4444",
                                    fontSize: "16px",
                                    fontWeight: "bold",
                                  }}
                                >
                                  !
                                </span>
                              )}
                            </span>
                          ) : (
                            <span className="schedule-meeting-link">
                              Schedule Meeting
                            </span>
                          )}
                        </td>
                        <td>
                          {recruit.last_contact_date
                            ? new Date(
                                recruit.last_contact_date
                              ).toLocaleDateString("en-US", {
                                month: "short",
                                day: "numeric",
                                year: "numeric",
                              })
                            : "N/A"}
                        </td>
                        <td
                          className={
                            !isLoadingMeetings &&
                            (lastMeeting ||
                              (recruit.last_meeting_date && !lastMeeting))
                              ? "clickable-cell"
                              : ""
                          }
                          onClick={e => {
                            e.stopPropagation();
                            if (!isLoadingMeetings) {
                              // If meeting has been reported (last_meeting_date >= lastMeeting.time), go to detail view
                              if (
                                lastMeeting &&
                                recruit.last_meeting_date &&
                                new Date(recruit.last_meeting_date) >=
                                  new Date(lastMeeting.time)
                              ) {
                                setViewingRecruitDetail(recruit);
                                window.history.pushState(
                                  {
                                    page: "recruit-detail",
                                    recruitId: recruit.id,
                                  },
                                  "",
                                  window.location.pathname
                                );
                              }
                              // If meeting hasn't been reported yet, open report modal
                              else if (lastMeeting) {
                                handleReportMeeting(recruit);
                              }
                              // If no lastMeeting but has last_meeting_date, go to detail view
                              else if (
                                recruit.last_meeting_date &&
                                !lastMeeting
                              ) {
                                setViewingRecruitDetail(recruit);
                                window.history.pushState(
                                  {
                                    page: "recruit-detail",
                                    recruitId: recruit.id,
                                  },
                                  "",
                                  window.location.pathname
                                );
                              }
                            }
                          }}
                        >
                          {isLoadingMeetings ? (
                            <span
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "6px",
                                color: "#6b7280",
                              }}
                            >
                              <div
                                className="loading-spinner"
                                style={{
                                  width: "16px",
                                  height: "16px",
                                  border: "2px solid #e5e7eb",
                                  borderTopColor: "#3b82f6",
                                }}
                              ></div>
                              <span style={{ fontSize: "14px" }}>
                                Loading...
                              </span>
                            </span>
                          ) : lastMeeting ? (
                            <span
                              style={{
                                color:
                                  recruit.last_meeting_date &&
                                  new Date(recruit.last_meeting_date) >=
                                    new Date(lastMeeting.time)
                                    ? "inherit"
                                    : "#dc2626",
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "6px",
                              }}
                            >
                              <span>
                                {new Date(lastMeeting.time).toLocaleDateString(
                                  "en-US",
                                  {
                                    month: "short",
                                    day: "numeric",
                                    year: "numeric",
                                  }
                                )}{" "}
                                {new Date(lastMeeting.time).toLocaleTimeString(
                                  "en-US",
                                  {
                                    hour: "numeric",
                                    minute: "2-digit",
                                    hour12: true,
                                  }
                                )}
                              </span>
                              {(!recruit.last_meeting_date ||
                                new Date(recruit.last_meeting_date) <
                                  new Date(lastMeeting.time)) && (
                                <button
                                  className="meeting-not-logged-indicator"
                                  title="Click to log meeting"
                                  style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    padding: "4px 8px",
                                    border: "none",
                                    borderRadius: "4px",
                                    backgroundColor: "#d0521f",
                                    color: "white",
                                    fontSize: "11px",
                                    fontWeight: "600",
                                    cursor: "pointer",
                                    transition: "all 0.2s",
                                  }}
                                  onMouseEnter={e => {
                                    e.currentTarget.style.backgroundColor =
                                      "#b9491c";
                                    e.currentTarget.style.transform =
                                      "scale(1.05)";
                                  }}
                                  onMouseLeave={e => {
                                    e.currentTarget.style.backgroundColor =
                                      "#d0521f";
                                    e.currentTarget.style.transform =
                                      "scale(1)";
                                  }}
                                >
                                  Report
                                </button>
                              )}
                            </span>
                          ) : recruit.last_meeting_date ? (
                            new Date(
                              recruit.last_meeting_date
                            ).toLocaleDateString("en-US", {
                              month: "short",
                              day: "numeric",
                              year: "numeric",
                            })
                          ) : (
                            "N/A"
                          )}
                        </td>
                        <td
                          onClick={e => e.stopPropagation()}
                          style={{ textAlign: "center" }}
                        >
                          <button
                            className="icon-action-btn"
                            onClick={e => {
                              e.stopPropagation();
                              if (hasDeal) {
                                navigate(
                                  `/deals/${recruitDeals[0].deal_id}/edit`
                                );
                              } else {
                                navigate(`/deals/add?lead_id=${recruit.id}`);
                              }
                            }}
                            title={hasDeal ? "Edit Deal" : "Create Deal"}
                            style={{
                              width: "32px",
                              height: "32px",
                              borderRadius: "50%",
                              border: "none",
                              backgroundColor: hasDeal ? "#16A34A" : "#D2B48C",
                              color: "white",
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "center",
                              cursor: "pointer",
                              transition: "all 0.2s",
                            }}
                            onMouseEnter={e => {
                              e.currentTarget.style.backgroundColor = hasDeal
                                ? "#22C55E"
                                : "#DEC9A3";
                              e.currentTarget.style.transform = "scale(1.1)";
                            }}
                            onMouseLeave={e => {
                              e.currentTarget.style.backgroundColor = hasDeal
                                ? "#16A34A"
                                : "#D2B48C";
                              e.currentTarget.style.transform = "scale(1)";
                            }}
                          >
                            <span
                              className="material-icons"
                              style={{ fontSize: "18px" }}
                            >
                              handshake
                            </span>
                          </button>
                        </td>
                        <td
                          onClick={e => e.stopPropagation()}
                          style={{ textAlign: "center" }}
                        >
                          {hasDocs ? (
                            <button
                              className="icon-action-btn"
                              onClick={e => {
                                e.stopPropagation();
                                setSelectedRecruit(recruit);
                                setIsManageDocumentsModalOpen(true);
                              }}
                              title="Manage Documents"
                              style={{
                                width: "32px",
                                height: "32px",
                                borderRadius: "50%",
                                border: "none",
                                backgroundColor: "#10b981",
                                color: "white",
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                cursor: "pointer",
                                transition: "all 0.2s",
                              }}
                              onMouseEnter={e => {
                                e.currentTarget.style.backgroundColor =
                                  "#34d399";
                                e.currentTarget.style.transform = "scale(1.1)";
                              }}
                              onMouseLeave={e => {
                                e.currentTarget.style.backgroundColor =
                                  "#10b981";
                                e.currentTarget.style.transform = "scale(1)";
                              }}
                            >
                              <span
                                className="material-icons"
                                style={{ fontSize: "18px" }}
                              >
                                folder
                              </span>
                            </button>
                          ) : hasDeal ? (
                            <button
                              className="icon-action-btn"
                              onClick={e => {
                                e.stopPropagation();
                                setSelectedRecruit(recruit);
                                setIsManageDocumentsModalOpen(true);
                              }}
                              title="Add Documents"
                              style={{
                                width: "32px",
                                height: "32px",
                                borderRadius: "50%",
                                border: "none",
                                backgroundColor: "#9CA3AF",
                                color: "white",
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                cursor: "pointer",
                                transition: "all 0.2s",
                              }}
                              onMouseEnter={e => {
                                e.currentTarget.style.backgroundColor =
                                  "#B0B7C0";
                                e.currentTarget.style.transform = "scale(1.1)";
                              }}
                              onMouseLeave={e => {
                                e.currentTarget.style.backgroundColor =
                                  "#9CA3AF";
                                e.currentTarget.style.transform = "scale(1)";
                              }}
                            >
                              <span
                                className="material-icons"
                                style={{ fontSize: "18px" }}
                              >
                                add
                              </span>
                            </button>
                          ) : null}
                        </td>
                        <td
                          className="actions-cell"
                          onClick={e => e.stopPropagation()}
                          style={{
                            display: "flex",
                            gap: "0.5rem",
                            alignItems: "center",
                          }}
                        >
                          {/* TODO update these permissions for manager level */}
                          {(() => {
                            // Check if the logged-in user is the recruiter for this lead
                            const currentUserName =
                              `${user?.firstName} ${user?.lastName}`.trim();
                            const isCurrentUserRecruiter =
                              recruit.recruiter_name === currentUserName;

                            // If not the recruiter, show empty cell or a message
                            if (!isCurrentUserRecruiter) {
                              return (
                                <span
                                  style={{
                                    color: "#6b7280",
                                    fontSize: "14px",
                                    fontStyle: "italic",
                                  }}
                                >
                                  —
                                </span>
                              );
                            }

                            // If the user is the recruiter, show all action buttons
                            return (
                              <>
                                {/* Contact Icon */}
                                <button
                                  className="icon-action-btn"
                                  onClick={e => {
                                    e.stopPropagation();
                                    setSelectedRecruit(recruit);
                                    setIsLogContactModalOpen(true);
                                  }}
                                  title="Log Contact"
                                  style={{
                                    width: "32px",
                                    height: "32px",
                                    borderRadius: "50%",
                                    border: "none",
                                    backgroundColor: "#DE8662",
                                    color: "white",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    cursor: "pointer",
                                    transition: "all 0.2s",
                                  }}
                                  onMouseEnter={e => {
                                    e.currentTarget.style.backgroundColor =
                                      "#E7A58B";
                                    e.currentTarget.style.transform =
                                      "scale(1.1)";
                                  }}
                                  onMouseLeave={e => {
                                    e.currentTarget.style.backgroundColor =
                                      "#DE8662";
                                    e.currentTarget.style.transform =
                                      "scale(1)";
                                  }}
                                >
                                  <span
                                    className="material-icons"
                                    style={{ fontSize: "18px" }}
                                  >
                                    phone
                                  </span>
                                </button>

                                {/* Meeting Icon */}
                                <button
                                  className="icon-action-btn"
                                  onClick={e => {
                                    e.stopPropagation();
                                    handleScheduleMeeting(recruit);
                                  }}
                                  title="Schedule Meeting"
                                  style={{
                                    width: "32px",
                                    height: "32px",
                                    borderRadius: "50%",
                                    border: "none",
                                    backgroundColor: "#A78BFA",
                                    color: "white",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    cursor: "pointer",
                                    transition: "all 0.2s",
                                  }}
                                  onMouseEnter={e => {
                                    e.currentTarget.style.backgroundColor =
                                      "#C4B5FD";
                                    e.currentTarget.style.transform =
                                      "scale(1.1)";
                                  }}
                                  onMouseLeave={e => {
                                    e.currentTarget.style.backgroundColor =
                                      "#A78BFA";
                                    e.currentTarget.style.transform =
                                      "scale(1)";
                                  }}
                                >
                                  <span
                                    className="material-icons"
                                    style={{ fontSize: "18px" }}
                                  >
                                    event
                                  </span>
                                </button>

                                {/* Add to List Icon - Coral */}
                                <button
                                  className="icon-action-btn"
                                  onClick={e => {
                                    e.stopPropagation();
                                    setSelectedRecruit(recruit);
                                    setIsAddToListModalOpen(true);
                                  }}
                                  title="Add to List"
                                  style={{
                                    width: "32px",
                                    height: "32px",
                                    borderRadius: "50%",
                                    border: "none",
                                    backgroundColor: "#F87171",
                                    color: "white",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    cursor: "pointer",
                                    transition: "all 0.2s",
                                  }}
                                  onMouseEnter={e => {
                                    e.currentTarget.style.backgroundColor =
                                      "#FCA5A5";
                                    e.currentTarget.style.transform =
                                      "scale(1.1)";
                                  }}
                                  onMouseLeave={e => {
                                    e.currentTarget.style.backgroundColor =
                                      "#F87171";
                                    e.currentTarget.style.transform =
                                      "scale(1)";
                                  }}
                                >
                                  <span
                                    className="material-icons"
                                    style={{ fontSize: "18px" }}
                                  >
                                    playlist_add
                                  </span>
                                </button>

                                {/* Mark Cold Icon */}
                                <button
                                  className="icon-action-btn"
                                  onClick={e => handleMarkCold(recruit, e)}
                                  title="Mark as Cold"
                                  style={{
                                    width: "32px",
                                    height: "32px",
                                    borderRadius: "50%",
                                    border: "none",
                                    backgroundColor: "#60A5FA",
                                    color: "white",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    cursor: "pointer",
                                    transition: "all 0.2s",
                                  }}
                                  onMouseEnter={e => {
                                    e.currentTarget.style.backgroundColor =
                                      "#93C5FD";
                                    e.currentTarget.style.transform =
                                      "scale(1.1)";
                                  }}
                                  onMouseLeave={e => {
                                    e.currentTarget.style.backgroundColor =
                                      "#60A5FA";
                                    e.currentTarget.style.transform =
                                      "scale(1)";
                                  }}
                                >
                                  <span
                                    className="material-icons"
                                    style={{ fontSize: "18px" }}
                                  >
                                    ac_unit
                                  </span>
                                </button>

                                {/* Convert to Rep Icon - Only show for Signed leads */}
                                {recruit.status === "Signed" && (
                                  <button
                                    className="icon-action-btn"
                                    onClick={e => {
                                      e.stopPropagation();
                                      // Navigate to Add Rep page with lead data pre-populated

                                      const leadData = {
                                        firstName: recruit.first_name,
                                        lastName: recruit.last_name,
                                        email: recruit.email || "",
                                        phoneNumber: recruit.phone_number || "",
                                        instagram: recruit.instagram_username,
                                        linkedinProfile:
                                          recruit.linkedin_profile,
                                      };
                                      navigate("/reps/add", {
                                        state: { leadData },
                                      });
                                    }}
                                    title="Convert to Rep"
                                    style={{
                                      width: "32px",
                                      height: "32px",
                                      borderRadius: "50%",
                                      border: "none",
                                      backgroundColor: "#8B5CF6",
                                      color: "white",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      cursor: "pointer",
                                      transition: "all 0.2s",
                                    }}
                                    onMouseEnter={e => {
                                      e.currentTarget.style.backgroundColor =
                                        "#A78BFA";
                                      e.currentTarget.style.transform =
                                        "scale(1.1)";
                                    }}
                                    onMouseLeave={e => {
                                      e.currentTarget.style.backgroundColor =
                                        "#8B5CF6";
                                      e.currentTarget.style.transform =
                                        "scale(1)";
                                    }}
                                  >
                                    <span
                                      className="material-icons"
                                      style={{ fontSize: "18px" }}
                                    >
                                      person
                                    </span>
                                  </button>
                                )}

                                {/* Documents Icon - moved to its own Docs
                                    column; kept here commented out for now */}
                                {/* <button
                                  className="icon-action-btn"
                                  onClick={e => {
                                    e.stopPropagation();
                                    setSelectedRecruit(recruit);
                                    setIsManageDocumentsModalOpen(true);
                                  }}
                                  title="Manage Documents"
                                  style={{
                                    width: "32px",
                                    height: "32px",
                                    borderRadius: "50%",
                                    border: "none",
                                    backgroundColor: "#10b981",
                                    color: "white",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    cursor: "pointer",
                                    transition: "all 0.2s",
                                  }}
                                >
                                  <span
                                    className="material-icons"
                                    style={{ fontSize: "18px" }}
                                  >
                                    folder
                                  </span>
                                </button> */}
                              </>
                            );
                          })()}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {/* Loading overlay for when there are existing leads */}
              {/* {isLoadingLeads && realLeads.length > 0 && (
                <div
                  style={{
                    position: "fixed",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: "rgba(255, 255, 255, 0.8)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    zIndex: 9999,
                  }}
                >
                  <div
                    className="loading-container"
                    style={{ backgroundColor: "transparent" }}
                  >
                    <div className="loading-spinner"></div>
                    <div className="loading-text">Refreshing leads...</div>
                  </div>
                </div>
              )} */}
            </>
          )}
          {!isLoadingLeads && activeRecruits.length === 0 && (
            <div className="no-recruits">No recruits in this stage</div>
          )}
        </div>
      </div>

      {/* Meeting Scheduler Modal */}
      <CalendarScheduler
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        recruit={selectedRecruit}
        existingEventId={
          selectedRecruit ? getExistingEventId(selectedRecruit.id) : null
        }
        existingMeeting={
          selectedRecruit ? scheduledMeetings[selectedRecruit.id] : null
        }
      />

      {/* Add Lead Modal */}
      <AddLeadModal
        isOpen={isAddLeadModalOpen}
        onClose={() => setIsAddLeadModalOpen(false)}
        onLeadAdded={handleLeadAdded}
      />

      {/* Meeting Report Modal */}
      <MeetingReportModal
        isOpen={isReportModalOpen}
        onClose={handleCloseReportModal}
        recruit={selectedRecruit}
        meeting={
          selectedRecruit
            ? scheduledMeetings[selectedRecruit.id] ||
              lastMeetings[selectedRecruit.id]
            : null
        }
        onScheduleNewMeeting={handleScheduleMeeting}
        onAdvanceStage={handleAdvanceStage}
        onDocumentSent={handleDocumentSent}
      />

      {/* Document Status Modal */}
      <DocumentStatusModal
        isOpen={isDocStatusModalOpen}
        onClose={() => setIsDocStatusModalOpen(false)}
        recruit={selectedRecruit}
      />

      {/* Send Docs Modal */}
      <SendDocsModal
        isOpen={isSendDocsModalOpen}
        onClose={documentWasSent => {
          setIsSendDocsModalOpen(false);
          if (documentWasSent && selectedRecruit) {
            handleDocumentSent(selectedRecruit);
          }
          setSelectedRecruit(null);
          setPreSelectedTemplateId(null); // Clear the pre-selected template
        }}
        recruit={selectedRecruit}
        onDocumentSent={handleDocumentSent}
        preSelectedTemplateId={preSelectedTemplateId}
      />

      {/* Meeting Details Modal */}
      <MeetingDetailsModal
        isOpen={isMeetingDetailsModalOpen}
        onClose={() => {
          setIsMeetingDetailsModalOpen(false);
          setSelectedRecruit(null);
        }}
        recruit={selectedRecruit}
        meeting={
          selectedRecruit
            ? scheduledMeetings[selectedRecruit.id] ||
              lastMeetings[selectedRecruit.id]
            : null
        }
        onReschedule={handleScheduleMeeting}
        onLogMeeting={handleReportMeeting}
        onMeetingCancelled={async recruit => {
          // CRITICAL: Pause the auto-refresh interval to prevent it from re-adding the deleted meeting
          if (refreshIntervalRef.current) {
            clearInterval(refreshIntervalRef.current);
            refreshIntervalRef.current = null;
          }

          // OPTIMISTIC UPDATE: Remove the meeting immediately from UI
          setScheduledMeetings(prev => {
            const updated = { ...prev };
            delete updated[recruit.id];
            return updated;
          });

          // Wait for backend cancellation to fully commit
          // Longer delay to ensure cancellation event is saved to database
          await new Promise(resolve => setTimeout(resolve, 2500));

          // Refresh leads first, then meetings with the FRESH leads data
          setIsLoadingLeads(true);
          try {
            const freshLeads = await fetchLeads();
            // Use the fresh leads data that was just fetched, not the stale state
            await fetchScheduledMeetings(freshLeads);
          } finally {
            setIsLoadingLeads(false);
          }

          // Refresh history in detail view if viewing that recruit
          if (viewingRecruitDetail && viewingRecruitDetail.id === recruit.id) {
            recruitDetailRef.current?.refreshHistory();
          }
        }}
      />

      {/* Log Contact Modal */}
      <LogContactModal
        isOpen={isLogContactModalOpen}
        onClose={() => {
          setIsLogContactModalOpen(false);
          setSelectedRecruit(null);
        }}
        recruit={selectedRecruit}
        onContactLogged={async recruit => {
          setIsLoadingLeads(true);
          try {
            await fetchLeads();
          } finally {
            setIsLoadingLeads(false);
          }

          // Refresh history in detail view if viewing that recruit
          if (viewingRecruitDetail && viewingRecruitDetail.id === recruit.id) {
            recruitDetailRef.current?.refreshHistory();
          }
        }}
      />

      {/* Mark Cold Modal */}
      <MarkColdModal
        isOpen={isMarkColdModalOpen}
        onClose={() => {
          setIsMarkColdModalOpen(false);
          setSelectedRecruit(null);
        }}
        recruit={selectedRecruit}
        onConfirmMarkCold={handleConfirmMarkCold}
      />

      {/* Document Signing Modal */}
      <DocumentSigningModal
        isOpen={isDocumentSigningModalOpen}
        onClose={() => {
          setIsDocumentSigningModalOpen(false);
          setSelectedRecruit(null);
        }}
        recruit={selectedRecruit}
        onDocumentsSigned={handleDocumentsSigned}
      />

      {/* Sent Documents Modal */}
      <SentDocumentsModal
        isOpen={isSentDocumentsModalOpen}
        onClose={() => {
          setIsSentDocumentsModalOpen(false);
          setSelectedRecruit(null);
        }}
        recruit={selectedRecruit}
        onDocumentsSent={handleDocumentSent}
      />

      {/* Manage Documents Modal */}
      <ManageDocumentsModal
        isOpen={isManageDocumentsModalOpen}
        onClose={() => {
          setIsManageDocumentsModalOpen(false);
          // DON'T clear selectedRecruit here - let the action handler or SendDocsModal handle it
          // This prevents race condition where selectedRecruit is cleared before SendDocsModal opens
        }}
        recruit={selectedRecruit}
        onAction={handleDocumentAction}
      />

      {/* Add to List Modal */}
      <AddToListModal
        isOpen={isAddToListModalOpen}
        onClose={() => {
          setIsAddToListModalOpen(false);
          setSelectedRecruit(null);
        }}
        recruit={selectedRecruit}
        onSuccess={async () => {
          // Clear cache to force fresh data
          localStorage.removeItem("recruiting_leads_cache");
          localStorage.removeItem("recruiting_meetings_cache");

          // Set loading state
          setIsLoadingLeads(true);

          try {
            // Refresh leads with fresh data from server
            await fetchLeads();
          } finally {
            setIsLoadingLeads(false);
          }
        }}
      />

      {/* Floating Action Button */}
      <button
        className="fab-add-lead"
        onClick={() => setIsAddLeadModalOpen(true)}
        title="Add Lead"
      >
        <span className="material-icons">add</span>
      </button>
    </PageContent>
  );
}

export default RecruitingPage;
